import { fill, t } from '../strings';
import { storedNameExtension } from './storedName';
import { CREDIT_APPLICATION_FILE_TYPE_NAME, CREDIT_REFERENCE_FILE_TYPE_NAME, EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME, HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME, OFFER_LETTER_FILE_TYPE_NAME, TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME, W9_FILE_TYPE_NAME } from './seededFileTypes';

/**
 * 🔒 The standard forms (decision 7850; T3170): the types whose file takes a display name the application writes at
 * its first upload. 🔨 Named by the type's name, because no `FileType` column marks a standard form; the Settings
 * switch the owner asked for waits on one (piece 9, Open issues).
 */
export const STANDARD_FORM_FILE_TYPE_NAMES: readonly string[] = [W9_FILE_TYPE_NAME, TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME, CREDIT_APPLICATION_FILE_TYPE_NAME, CREDIT_REFERENCE_FILE_TYPE_NAME, EMPLOYMENT_AGREEMENT_FILE_TYPE_NAME, OFFER_LETTER_FILE_TYPE_NAME, HANDBOOK_ACKNOWLEDGEMENT_FILE_TYPE_NAME];

/**
 * 🔒 The standard forms whose own record carries the form's date (decision 9540; T3358): the Tax Exempt Certificate,
 * whose row holds its effective date. Their name takes that date, and the upload asks nothing.
 */
export const STANDARD_FORMS_DATED_BY_RECORD: readonly string[] = [TAX_EXEMPT_CERTIFICATE_FILE_TYPE_NAME];

/** 🔒 Decision 9540: the upload fields that carry a standard form's own date and its optional name, beside the file. */
export const FORM_DATE_FIELD = 'formDate';
export const FORM_NAME_FIELD = 'formName';

/** 🔨 Decision 9540: a typed form name longer than this is refused, so a name stays one a file system keeps whole. */
export const FORM_NAME_MAX_LENGTH = 80;

/** 🔒 Decision 11301: the upload field a file's own name rides in, the name box the upload window draws for each file. */
export const FILE_NAME_FIELD = 'fileName';

/** 🔨 Decision 11301: a file name typed in the upload window longer than this is refused; most file systems keep 255 characters. */
export const FILE_NAME_MAX_LENGTH = 255;

/** 🔒 Decision 11301: the read of the name a standard form's entity takes on a record, which the window's name box fills from. */
export const STANDARD_FORM_ENTITY_PATH = '/files/standard-form-entity';

const sameName = (list: readonly string[], typeName: string): boolean => {
  const name = typeName.trim().toLowerCase();
  return list.some((one) => one.toLowerCase() === name);
};

/**
 * Whether a type of this name is a standard form, compared without case or surrounding spaces. 🔒 A type marked an
 * employment form is one whatever its name (decision 9680), so the caller that holds the type's mark hands it in.
 */
export function isStandardForm(typeName: string, employmentForm = false): boolean {
  return employmentForm || sameName(STANDARD_FORM_FILE_TYPE_NAMES, typeName);
}

/**
 * 🔒 Whether the upload of a type asks for the form's date and its optional name (decision 9540): a standard form whose
 * record carries no date of its own, the W-9, the Credit Application and the Credit Reference.
 */
export function standardFormAsksDate(typeName: string, employmentForm = false): boolean {
  return isStandardForm(typeName, employmentForm) && !sameName(STANDARD_FORMS_DATED_BY_RECORD, typeName);
}

/**
 * 🔒 Whether a file's line in the Upload window draws a Form Name box (decision 11562): a standard form that asks its
 * date and is not an employment form, so a Credit Application's or a Credit Reference's bank name; every other type draws none.
 */
export function standardFormTakesName(typeName: string, employmentForm = false): boolean {
  return !employmentForm && standardFormAsksDate(typeName, false);
}

/**
 * The year of a form's date written as a calendar day, `yyyy-mm-dd`; null where the text is no such day, so the
 * caller refuses it or takes today's.
 */
export function formDateYear(formDate: string | null | undefined): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((formDate ?? '').trim());
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? year : null;
}

/**
 * 🔒 Decision 9540, amended by 9640: why a typed form date or form name is refused, in one sentence; null where both
 * stand. `today` is the caller's calendar day, `yyyy-mm-dd`, and a form date after it is refused, because no standard
 * form is signed in the future. A caller that has no day to give checks the calendar day alone.
 */
export function standardFormFieldsProblem(fields: { formDate?: string | null; formName?: string | null; today?: string | null }): string | null {
  const date = (fields.formDate ?? '').trim();
  if (date !== '' && formDateYear(date) === null) return t('upload.formDate.calendarDay');
  if (date !== '' && fields.today && formDateYear(fields.today) !== null && date > fields.today.trim()) return t('upload.formDate.future');
  if ((fields.formName ?? '').trim().length > FORM_NAME_MAX_LENGTH) return fill(t('upload.formName.tooLong'), { max: FORM_NAME_MAX_LENGTH });
  return null;
}

/**
 * 🔒 Decision 11301: why a document's date typed in the upload window is refused, in one sentence; null where it
 * stands. A standard form's refusal is decision 9640's; any other document's says the same of a document.
 */
export function documentDateProblem(fields: { date?: string | null; today?: string | null; standardForm: boolean }): string | null {
  if (fields.standardForm) return standardFormFieldsProblem({ formDate: fields.date ?? null, today: fields.today ?? null });
  const date = (fields.date ?? '').trim();
  if (date !== '' && formDateYear(date) === null) return 'The document date is a calendar day, written as year, month and day.';
  if (date !== '' && fields.today && formDateYear(fields.today) !== null && date > fields.today.trim()) return t('upload.documentDate.future');
  return null;
}

/**
 * The record a standard form is filed on, as its name reads it: a Company by its display name, a Person by their name
 * and their employer's, and a Site or a Location by the company and the place's city.
 */
export type StandardFormEntity =
  | { kind: 'company'; company: string }
  | { kind: 'person'; person: string; company: string | null }
  | { kind: 'place'; company: string | null; place: string | null };

/** The characters a file system refuses in a name, and the control characters, which the name drops. */
const REFUSED = new Set(['\\', '/', ':', '*', '?', '"', '<', '>', '|']);

const refused = (char: string): boolean => REFUSED.has(char) || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127;

/** 🔨 The " · " that joins the name's segments is dropped from a part, so a typed form name never reads as an extra segment (T3363). */
const SEGMENT_JOIN = / · /g;

/** The text with the characters a file system refuses blanked; 🔒 the form name alone also loses " · ", which joins the name's parts (T3448). */
const clean = (text: string | null | undefined, dropJoin = false): string => {
  const blanked = [...(text ?? '')].map((char) => (refused(char) ? ' ' : char)).join('');
  return (dropJoin ? blanked.replace(SEGMENT_JOIN, ' ') : blanked).replace(/\s+/g, ' ').trim();
};

/** The entity's part of the name, its two halves joined with a comma; null when nothing in it can be read. */
export function standardFormEntityName(entity: StandardFormEntity): string | null {
  const parts = entity.kind === 'company' ? [entity.company] : entity.kind === 'person' ? [entity.person, entity.company] : [entity.company, entity.place];
  const named = parts.map((part) => clean(part)).filter((part) => part !== '');
  return named.length === 0 ? null : named.join(', ');
}

/**
 * 🔒 The display name a standard form's file takes (decision 7850): the entity's name, the form type's name and the
 * year joined with " · ", carrying the typed file's extension, so *Acme Supply · W-9 · 2026.pdf*. The year is the
 * form's own date's (decision 9540), and a form name the person typed joins after the type, so *Acme Supply · Credit
 * Reference · Smith Bank · 2024.pdf*. A type that is not a standard form, or an entity with no readable name,
 * answers null, and the typed name stands.
 */
export function standardFormFileName(facts: { entity: StandardFormEntity | null; typeName: string; year: number; typedName: string; formName?: string | null; employmentForm?: boolean }): string | null {
  if (!isStandardForm(facts.typeName, facts.employmentForm === true) || !facts.entity) return null;
  const entity = standardFormEntityName(facts.entity);
  const typeName = clean(facts.typeName);
  if (!entity || typeName === '') return null;
  const formName = clean(facts.formName, true);
  return `${[entity, typeName, ...(formName === '' ? [] : [formName]), String(facts.year)].join(' · ')}${storedNameExtension(facts.typedName)}`;
}

/**
 * 🔒 Decision 11301: the name a file's line in the upload window opens with. A standard form takes the name decision
 * 7850 writes, for the year of the line's date, with the line's form name after the type (decision 11562); any other file, or a form whose record's name cannot be read, its own.
 */
export function uploadLineName(facts: { typeName: string; employmentForm?: boolean; entity: StandardFormEntity | null; date: string; typedName: string; formName?: string | null }): string {
  const year = formDateYear(facts.date);
  if (year === null) return facts.typedName;
  return standardFormFileName({ entity: facts.entity, typeName: facts.typeName, year, typedName: facts.typedName, formName: facts.formName ?? null, employmentForm: facts.employmentForm === true }) ?? facts.typedName;
}

/**
 * 🔒 Decision 11301: a name typed in the upload window as it is filed. The characters a file system refuses are
 * dropped, runs of space fold to one, and the file's own extension is added where the name does not end with it.
 */
export function cleanUploadFileName(name: string, typedName: string): string {
  const cleaned = clean(name);
  const extension = storedNameExtension(typedName);
  if (cleaned === '' || extension === '' || cleaned.toLowerCase().endsWith(extension.toLowerCase())) return cleaned;
  return `${cleaned}${extension}`;
}

/**
 * 🔨 Decision 11301: why a typed file name is refused, in one sentence; null where it stands. Where the file's own name
 * is given, the ceiling is read on the name as it is filed, with the extension it adds (row T3673).
 */
export function uploadFileNameProblem(name: string | null | undefined, typedName?: string): string | null {
  const cleaned = clean(name);
  if (cleaned === '') return t('upload.fileName.required');
  const filed = typedName === undefined ? cleaned : cleanUploadFileName(cleaned, typedName);
  if (filed.length > FILE_NAME_MAX_LENGTH) return fill(t('upload.fileName.tooLong'), { limit: FILE_NAME_MAX_LENGTH });
  return null;
}
